import {
  QueryKey,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient
} from '@tanstack/react-query';
import {
  ApprovalWorkflowControllerGetAssetByAwParams,
  AssetControllerCountAssetsParams,
  AssetControllerGetAssetParams,
  AssetControllerListAssetsParams,
  AssetItemDto,
  AssetReviewControllerGetAssetByReviewParams,
  CopyAssetsDto,
  MoveAssetToFolderDto,
  PageDto
} from '@api/Api';
import { copyAssets, moveAsset } from '@api/Asset';
import { apiClient } from '@api/client';
import { getItemPlaceholderFromListQuery } from '@hooks/queries/helpers';

export function useAssetQuery(
  params: AssetControllerGetAssetParams,
  options?: {
    enabled?: boolean;
    keepPreviousData?: boolean;
    placeholderData?: AssetItemDto;
  }
) {
  return useQuery({
    ...(options ?? {}),
    queryKey: ['assets', 'item', params.assetId, params],
    queryFn: async ({ signal }) => {
      const { data } = await apiClient.asset.assetControllerGetAsset(params, {
        signal
      });
      return data.asset;
    },
    placeholderData: () => {
      if (options?.placeholderData) return options.placeholderData;
      const asset = getItemPlaceholderFromListQuery<AssetItemDto>(
        ['assets', 'list'],
        params.assetId
      );
      if (!asset) return undefined;
      return asset;
    }
  });
}

export function useAssetsQuery(
  params: AssetControllerListAssetsParams,
  options?: {
    enabled?: boolean;
    keepPreviousData?: boolean;
    placeholderData?: PageDto & {
      edges: {
        node: AssetItemDto;
        cursor: string;
      }[];
    };
  }
) {
  return useInfiniteQuery({
    ...(options ?? {}),
    queryKey: ['assets', 'list', params],
    queryFn: async ({ pageParam, signal }) => {
      const { data } = await apiClient.asset.assetControllerListAssets(
        { ...params, after: pageParam },
        { signal }
      );
      return data;
    },
    getNextPageParam: (lastPage) =>
      lastPage.hasNext ? lastPage.endCursor : undefined,
    placeholderData: options?.placeholderData
      ? {
          pages: [options.placeholderData],
          pageParams: [
            options.placeholderData.hasNext
              ? options.placeholderData.endCursor
              : undefined
          ]
        }
      : undefined
  });
}

export function useAssetsCountQuery(
  params: AssetControllerCountAssetsParams,
  options?: {
    enabled?: boolean;
    keepPreviousData?: boolean;
    placeholderData?: number;
  }
) {
  return useQuery({
    ...(options ?? {}),
    queryKey: ['assets', 'count', params],
    queryFn: async ({ signal }) => {
      const { data } = await apiClient.asset.assetControllerCountAssets(
        params,
        { signal }
      );
      return data.count;
    }
  });
}

export function useAssetByAwQuery(
  params: ApprovalWorkflowControllerGetAssetByAwParams,
  options?: {
    enabled?: boolean;
    keepPreviousData?: boolean;
    placeholderData?: AssetItemDto;
  }
) {
  return useQuery({
    ...(options ?? {}),
    queryKey: ['assets', 'item', params.assetId, 'by-aw', params],
    queryFn: async ({ signal }) => {
      const { data } =
        await apiClient.approvalWorkflow.approvalWorkflowControllerGetAssetByAw(
          params,
          { signal }
        );
      return data.asset;
    },
    placeholderData: () => {
      if (options?.placeholderData) return options.placeholderData;
      const asset = getItemPlaceholderFromListQuery<AssetItemDto>(
        ['assets', 'list'],
        params.assetId
      );
      if (!asset) return undefined;
      return asset;
    }
  });
}

export function useAssetByReviewQuery(
  params: AssetReviewControllerGetAssetByReviewParams,
  options?: {
    enabled?: boolean;
    keepPreviousData?: boolean;
    placeholderData?: AssetItemDto;
  }
) {
  return useQuery({
    ...(options ?? {}),
    queryKey: ['assets', 'item', params.assetId, 'by-review', params],
    queryFn: async ({ signal }) => {
      const { data } =
        await apiClient.assetReview.assetReviewControllerGetAssetByReview(
          params,
          { signal }
        );
      return data.asset;
    },
    placeholderData: () => {
      if (options?.placeholderData) return options.placeholderData;
      const asset = getItemPlaceholderFromListQuery<AssetItemDto>(
        ['assets', 'list'],
        params.assetId
      );
      if (!asset) return undefined;
      return asset;
    }
  });
}

export function useCopyAssetsMutation({
  queryKey = undefined
}: {
  queryKey?: QueryKey;
}) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (data: CopyAssetsDto) => {
      return await copyAssets(data);
    },
    onSettled: () => {
      if (!queryKey) return;
      queryClient.invalidateQueries({ queryKey });
    }
  });

  return mutation;
}

export function useMoveAssetsMutation() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (data: MoveAssetToFolderDto) => {
      return await moveAsset(data);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['assets'] });
    }
  });
  return mutation;
}
